import React, { useEffect, useState, useCallback } from 'react';
import { FiRefreshCw, FiArrowLeft, FiSearch } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { getApprovals, getApproval, getEntry, submitApprovalAction, getUserEmail } from '../../utils/journalApi';
import StatusBadge from './StatusBadge';

const TIERS = ['','1','2','3','4'];
const ACTIONS = ['APPROVED','REJECTED','AMENDED'];

/* ── Approval Detail sub-view ── */
function ApprovalDetail({ approvalId, onBack }) {
  const [approval, setApproval] = useState(null);
  const [entry, setEntry]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sodError, setSodError] = useState('');
  const [form, setForm] = useState({ action:'', comments:'' });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await getApproval(approvalId);
        setApproval(r.data);
        // Try to fetch linked entry by tracking_id or request_id
        const linkId = r.data.tracking_id || r.data.request_id;
        if (linkId) {
          try { const er = await getEntry(linkId); setEntry(er.data); } catch (_) {}
        }
      } catch (_) {}
      setLoading(false);
    })();
  }, [approvalId]);

  const handleSubmit = async () => {
    const errs = {};
    if (!form.action) errs.action = 'Required';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    setSodError('');
    try {
      await submitApprovalAction(approvalId, {
        action:    form.action,
        comments:  form.comments || undefined,
        acted_by:  getUserEmail(),
      });
      toast.success(`Decision recorded: ${form.action}`);
      onBack();
    } catch (err) {
      if (err.response?.status === 403) {
        setSodError(err.response?.data?.message || 'You cannot approve your own submission (SoD restriction).');
      } else if (err.response?.status === 409) {
        setSodError('This approval has already been actioned.');
      }
    }
    setSubmitting(false);
  };

  if (loading) return <div className="journal-loading"><div className="journal-spinner" /><span>Loading…</span></div>;
  if (!approval) return <div className="journal-empty">Approval not found.</div>;

  const isOverdue = approval.sla_deadline && new Date(approval.sla_deadline) < new Date();

  return (
    <div>
      <button className="j-back-link" onClick={onBack}><FiArrowLeft size={13} /> Back to approvals</button>

      {/* Approval info */}
      <div className="journal-panel">
        <div className="journal-panel-head">
          <h3>Approval — <code style={{ fontFamily:'monospace', fontSize:13 }}>{approvalId}</code></h3>
        </div>
        <div className="j-detail-grid">
          <div className="j-detail-item"><label>Tier</label><div className="val">Tier {approval.approval_tier ?? approval.tier}</div></div>
          <div className="j-detail-item"><label>Role</label><div className="val">{approval.approver_role ?? approval.role}</div></div>
          <div className="j-detail-item">
            <label>SLA Deadline</label>
            <div className={`val ${isOverdue ? 'sla-overdue' : ''}`}>
              {approval.sla_deadline ? new Date(approval.sla_deadline).toLocaleString() : '—'}
              {isOverdue && ' ⚠ Overdue'}
            </div>
          </div>
          <div className="j-detail-item">
            <label>Assigned At</label>
            <div className="val">{approval.assigned_at ? new Date(approval.assigned_at).toLocaleString() : '—'}</div>
          </div>
        </div>
      </div>

      {/* Linked entry */}
      {entry && (
        <div className="journal-panel">
          <div className="journal-panel-head"><h3>Linked Journal Entry</h3></div>
          <div className="j-detail-grid">
            <div className="j-detail-item"><label>Tracking ID</label><div className="val" style={{ fontFamily:'monospace', fontSize:12 }}>{entry.tracking_id}</div></div>
            <div className="j-detail-item"><label>Status</label><div className="val"><StatusBadge status={entry.status} /></div></div>
            <div className="j-detail-item"><label>Company</label><div className="val">{entry.company_code}</div></div>
            <div className="j-detail-item"><label>Posting Date</label><div className="val">{entry.posting_date}</div></div>
            <div className="j-detail-item"><label>Doc Type</label><div className="val">{entry.document_type}</div></div>
            <div className="j-detail-item"><label>Currency</label><div className="val">{entry.currency}</div></div>
            <div className="j-detail-item span2"><label>Submitted By</label><div className="val">{entry.submitted_by}</div></div>
          </div>
          {entry.line_items?.length > 0 && (
            <div className="journal-table-wrap" style={{ marginTop:12 }}>
              <table className="journal-table">
                <thead><tr><th>#</th><th>PK</th><th>GL Account</th><th>Amount</th><th>Cost Ctr</th><th>Item Text</th></tr></thead>
                <tbody>
                  {entry.line_items.map((l,i) => (
                    <tr key={i}>
                      <td>{i+1}</td>
                      <td>{l.posting_key === '40' ? 'Dr (40)' : 'Cr (50)'}</td>
                      <td><strong>{l.gl_account}</strong></td>
                      <td style={{ textAlign:'right' }}>{Number(l.amount).toLocaleString(undefined,{minimumFractionDigits:2})}</td>
                      <td className="journal-muted">{l.cost_center||'—'}</td>
                      <td className="journal-muted">{l.item_text||'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Action form */}
      <div className="journal-panel">
        <div className="journal-panel-head"><h3>Submit Decision</h3></div>
        {sodError && (
          <div className="journal-alert error" style={{ margin: '0 0 14px' }}>
            {sodError}
          </div>
        )}
        <div className="journal-field-grid">
          <div className="journal-field">
            <label>Decision *</label>
            <select value={form.action} onChange={e => setForm(f => ({...f, action:e.target.value}))}
              className={errors.action ? 'err' : ''}>
              <option value="">— Select —</option>
              {ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            {errors.action && <span className="journal-field-err">{errors.action}</span>}
          </div>
        </div>
        <div className="journal-field" style={{ marginTop:12 }}>
          <label>Comments</label>
          <textarea rows={3} value={form.comments} onChange={e => setForm(f => ({...f, comments:e.target.value}))}
            placeholder="Optional…" style={{ resize:'vertical' }} />
        </div>
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:16 }}>
          <button className="journal-btn ghost" onClick={onBack}>Cancel</button>
          <button className="journal-btn primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit Decision'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── List view ── */
export default function JournalApprovals() {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [selected, setSelected]   = useState(null);
  const [filters, setFilters]     = useState({ tier:'', company_code:'' });

  const load = useCallback(async (f = filters) => {
    setLoading(true);
    try {
      const params = {};
      if (f.tier) params.tier = f.tier;
      if (f.company_code) params.company_code = f.company_code;
      const res = await getApprovals(params);
      setApprovals(res.data.items ?? res.data);
    } catch (_) {}
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, []);

  if (selected) return <ApprovalDetail approvalId={selected} onBack={() => setSelected(null)} />;

  const isOverdue = (d) => d && new Date(d) < new Date();

  return (
    <div>
      <div className="journal-filters">
        <label>Tier
          <select className="journal-input" value={filters.tier}
            onChange={e => setFilters(f => ({...f, tier:e.target.value}))}>
            {TIERS.map(t => <option key={t} value={t}>{t ? `Tier ${t}` : '— All —'}</option>)}
          </select>
        </label>
        <label>Company Code
          <input className="journal-input" maxLength={4} placeholder="e.g. 1000" value={filters.company_code}
            onChange={e => setFilters(f => ({...f, company_code:e.target.value}))} style={{ width:120 }} />
        </label>
        <button className="journal-btn primary small" onClick={() => load()}>
          <FiSearch size={13} /> Search
        </button>
        <button className="journal-btn ghost small" onClick={() => { const f={tier:'',company_code:''}; setFilters(f); load(f); }}>
          <FiRefreshCw size={13} /> Reset
        </button>
      </div>

      <div className="journal-panel">
        <div className="journal-panel-head">
          <h3>Approvals Queue</h3>
          <span className="journal-muted" style={{ fontSize:'0.82rem' }}>{approvals.length} pending</span>
        </div>

        {loading ? (
          <div className="journal-loading"><div className="journal-spinner" /><span>Loading…</span></div>
        ) : approvals.length === 0 ? (
          <div className="journal-empty">No approvals pending.</div>
        ) : (
          <div className="journal-table-wrap">
            <table className="journal-table">
              <thead>
                <tr>
                  <th>Approval ID</th><th>Request ID</th><th>Tier</th>
                  <th>Approver Role</th><th>SLA Deadline</th><th>Assigned At</th>
                </tr>
              </thead>
              <tbody>
                {approvals.map(a => (
                  <tr key={a.approval_id} className="clickable" onClick={() => setSelected(a.approval_id)}>
                    <td style={{ fontFamily:'monospace', fontSize:11 }}>{a.approval_id}</td>
                    <td style={{ fontFamily:'monospace', fontSize:11 }}>{a.request_id ?? a.tracking_id}</td>
                    <td><strong>Tier {a.approval_tier ?? a.tier}</strong></td>
                    <td>{a.approver_role ?? a.role}</td>
                    <td className={isOverdue(a.sla_deadline) ? 'sla-overdue' : ''}>
                      {a.sla_deadline ? new Date(a.sla_deadline).toLocaleString() : '—'}
                      {isOverdue(a.sla_deadline) && ' ⚠'}
                    </td>
                    <td style={{ fontSize:11 }}>{a.assigned_at ? new Date(a.assigned_at).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
